import {
  MetricKpiTile,
  SectionHeading,
  panelErrorTitle,
} from "@/components/dashboard";
import { useConversion } from "@/hooks/useConversion";
import { useFunnel } from "@/hooks/useFunnel";
import { pickConversionTone, pickRateTone } from "@/lib/thresholds";
import type { TimeRange } from "@/lib/time-range";

interface ConversionSectionProps {
  range: TimeRange;
}

function fmtPct(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return "—";
  return `${n.toFixed(1)}%`;
}

export function ConversionSection({ range }: ConversionSectionProps) {
  const conv = useConversion(range);
  const funnel = useFunnel(range);

  const c = conv.data;
  const f = funnel.data;
  const convErr = conv.error ? panelErrorTitle(conv.error) : undefined;
  const funnelErr = funnel.error ? panelErrorTitle(funnel.error) : undefined;

  const abandonRate =
    f && f.starts > 0 ? (f.abandons / f.starts) * 100 : null;

  return (
    <section className="flex flex-col gap-3">
      <SectionHeading
        title="Conversion"
        frs={["FR-05"]}
        source="log-analytics"
        description="Overall visitor → reservation conversion ratios."
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <MetricKpiTile
          label="Reservation start rate"
          value={fmtPct(c?.startRatePct)}
          delta={c ? `${c.starts} starts / ${c.visitors} visits` : undefined}
          tone={pickRateTone(c?.startRatePct, { good: 20, warn: 10 })}
          hint="Share of homepage visits that opened the reservation flow."
          isLoading={conv.isLoading}
          errorTitle={convErr}
        />
        <MetricKpiTile
          label="Completion rate"
          value={fmtPct(c?.completionRatePct)}
          delta={c ? `${c.submits} submitted / ${c.starts} starts` : undefined}
          tone={pickRateTone(c?.completionRatePct, { good: 70, warn: 50 })}
          hint="Share of started reservations that were submitted successfully."
          isLoading={conv.isLoading}
          errorTitle={convErr}
        />
        <MetricKpiTile
          label="Overall conversion"
          value={fmtPct(c?.overallConversionPct)}
          delta={c ? `${c.submits} submitted / ${c.visitors} visits` : undefined}
          tone={pickConversionTone(c?.overallConversionPct)}
          hint="Share of homepage visits that ended in a submitted reservation. This is the headline business KPI."
          isLoading={conv.isLoading}
          errorTitle={convErr}
        />
        <MetricKpiTile
          label="Abandonment rate"
          value={fmtPct(abandonRate)}
          delta={
            f
              ? `${f.abandons.toLocaleString("en-US")} abandoned / ${f.starts} starts`
              : undefined
          }
          tone={
            abandonRate === null
              ? "neutral"
              : abandonRate >= 30
              ? "red"
              : abandonRate >= 15
              ? "amber"
              : "emerald"
          }
          hint="Share of started reservations that were dropped before submission."
          isLoading={funnel.isLoading}
          errorTitle={funnelErr}
        />
      </div>
    </section>
  );
}
